import { ErrorMessage } from "@/comps/message"
import {
	mimeTypes,
	MimeTypes,
} from "@/comps/simple-drop-area"
import { AITool, isImageOrVideo } from "controllers/editors"
import { useTranslation } from "next-i18next"
import { useContext, useState } from "react"
import { NotificationContext } from "./notification"

export interface UploadActions {
	onDrop: (files: File[]) => Promise<void>
	formats: MimeTypes[]
	openExplorer: () => void
	removeUploaded: () => void
	fileName: string
}

export function useUploadActions(
	tool: AITool | null,
	setFile: (file: File | null) => void,
): UploadActions | null {
	const { notify } = useContext(NotificationContext)
	const { t } = useTranslation()
	const [fileName, setFileName] = useState("")

	if (tool === null) {
		return null
	}
	const formats = getMimes(tool)

	const openExplorer = () => {
		const fileInput = document.createElement("input")

		fileInput.type = "file"
		fileInput.accept = formats.join(",")

		fileInput.onchange = async (_) => {
			if (fileInput.files) {
				const [file] = fileInput.files

				const isOk = await isImageOrVideo(file)

				if (!isOk) {
					notify(
						<ErrorMessage>
							{t("txt_wrong_file_uploaded")}
						</ErrorMessage>,
					)
				} else {
					setFile(file)
					setFileName(file.name)
				}
			}
		}
		fileInput.click()
	}

	const onDrop = async (files: File[]) => {
		const [file] = files

		if (!file) {
			return
		}

		const isOk = await isImageOrVideo(file)

		if (!isOk) {
			notify(
				<ErrorMessage>
					{t("txt_wrong_file_uploaded")}
				</ErrorMessage>,
			)
		} else {
			setFile(file)
			setFileName(file.name)
		}
	}

	const removeUploaded = () => {
		setFile(null)
	}

	return {
		openExplorer,
		formats,
		onDrop,
		removeUploaded,
		fileName,
	}
}

function getMimes(tool: AITool) {
	let mimeType: MimeTypes[] = [
		...mimeTypes["images"],
		...mimeTypes["videos"],
	]

	switch (tool) {
		case "ai_restyle":
			mimeType = [...mimeTypes["videos"]]
			break
		case "text_to_image":
		case "ai_photo":
			mimeType = [...mimeTypes["images"]]
			break
	}

	return mimeType
}

export const mimeTypeMapper: Partial<
	Record<string, string>
> = {
	"image/jpeg": ".jpg",
	"image/png": ".png",
	"image/gif": ".gif",
	"image/bmp": ".bmp",
	"image/webp": ".webp",
	"image/svg+xml": ".svg",
	"video/mp4": ".mp4",
	"video/x-msvideo": ".avi",
	"video/mpeg": ".mpeg",
	"video/quicktime": ".mov",
	"video/x-ms-wmv": ".wmv",
	"video/webm": ".webm",
	// "audio/mpeg": ".mp3",
	// "audio/wav": ".wav",
	// "audio/ogg": ".ogg",
	// "audio/aac": ".aac",
	// "audio/flac": ".flac",
	// "audio/webm": ".weba",
}
